import classNames from "classnames";
import { JSX, PropsWithoutRef, ReactNode, UIEventHandler, useCallback, useRef, useState } from "react";
import SectionTag from "~/components/SectionTag";
import styles from "./Testimonials.module.scss";
import logoAirius from "./airius.png";
import imageDoren from "./doren.jpg";
import imageErnest from "./ernest.jpg";
import logoForm from "./form.svg";
import logoGiz from "./giz.svg";
import imageJeremy from "./jeremy.jpg";
import imageOmer from "./omer.jpg";
import logoWorkintech from "./workintech.svg";

export type TestimonialsProps = Omit<PropsWithoutRef<JSX.IntrinsicElements["section"]>, "children">;

type State = {
  activeSlideIndex: number;
};

type Testimonial = {
  company: {
    link: string;
    logo: string;
    name: string;
  };
  content: ReactNode;
  person: {
    avatar: string;
    name: string;
    title: string;
  };
};

const testimonials: Array<Testimonial> = [
  {
    company: {
      link: "https://airius.com",
      logo: logoAirius,
      name: "Airius",
    },
    content: (
      <>
        Among all of the tools that we use, WebGazer is an important component in managing availability, performance and risk. It is our
        canary in the coal mine. We have come to depend on it and the reliable indications that it always provides.
      </>
    ),
    person: {
      avatar: imageErnest,
      name: "Ernest M. Park",
      title: "CISO and Managing Partner",
    },
  },
  {
    company: {
      link: "https://www.formhealth.co",
      logo: logoForm,
      name: "FORM",
    },
    content: (
      <>
        As a product and engineering leader, I want my team to know as soon as we have an outage or performance degradation so we can
        recover quickly. WebGazer requires almost no effort to set up and manage, and provides constant visibility into how our systems are
        performing to make sure they’re fast and reliable - and stay that way.
      </>
    ),
    person: {
      avatar: imageJeremy,
      name: "Jeremy Weiskotten",
      title: "VP of Engineering",
    },
  },
  {
    company: {
      link: "https://workintech.com.tr",
      logo: logoWorkintech,
      name: "Workintech",
    },
    content: (
      <>
        We&apos;ve been using WebGazer at Workintech, and it has been a game-changer. It allowed us to focus on developing high-quality
        content, while WebGazer takes care of ensuring our platform remains available to students at all times.
      </>
    ),
    person: {
      avatar: imageOmer,
      name: "Omer Tanriverdi",
      title: "Developer Instructor",
    },
  },
  {
    company: {
      link: "https://giz.de",
      logo: logoGiz,
      name: "GIZ",
    },
    content: (
      <>
        As someone who has worked on various projects across different domains, I&apos;ve found WebGazer to be an indispensable tool for
        ensuring the reliability of our online services. Their robust uptime monitoring and notification system have helped us identify and
        resolve issues promptly.
      </>
    ),
    person: {
      avatar: imageDoren,
      name: "Doren Calliku",
      title: "Mobility Engineer",
    },
  },
];

export default function Testimonials({ className, ...props }: TestimonialsProps) {
  const slider = useRef<HTMLDivElement>(null);
  const slides = useRef<Array<HTMLDivElement | null>>(testimonials.map(() => null));
  const switchingTo = useRef<number | null>(null);
  const [state, setState] = useState<State>({ activeSlideIndex: 0 });

  const switchSlide = useCallback((index: number) => {
    switchingTo.current = index;
    setState((s) => ({ ...s, activeSlideIndex: index }));
    const sliderCurrent = slider.current;

    if (sliderCurrent !== null) {
      let left: number = 0;

      if (index > 0) {
        const firstSlide = sliderCurrent.querySelector<HTMLDivElement>("& > div:first-child");
        const firstSlideCard = firstSlide?.querySelector<HTMLDivElement>(".card");

        if (firstSlide && firstSlideCard) {
          const slideCardWidth = firstSlideCard.scrollWidth;
          const sliderInlinePadding = firstSlideCard.offsetLeft - firstSlide.offsetLeft;

          left = sliderInlinePadding + (index * slideCardWidth);
        }
      }

      sliderCurrent.scrollTo({ behavior: "smooth", left });
    }
  }, []);

  const handleSliderScroll = useCallback<UIEventHandler<HTMLDivElement>>(() => {
    const sliderCurrent = slider.current;

    if (sliderCurrent !== null) {
      const secondSlide = sliderCurrent.querySelector<HTMLDivElement>("& > div:nth-child(2)");

      if (secondSlide) {
        const activeSlideIndex = Math.round(sliderCurrent.scrollLeft / secondSlide.scrollWidth);

        if (switchingTo.current === null) {
          setState((s) => ({ ...s, activeSlideIndex }));
        } else if (switchingTo.current === activeSlideIndex) {
          switchingTo.current = null;
        }
      }
    }
  }, []);

  return (
    <section {...props} className={classNames("pb-40", className)}>
      <div className="bg-body-tertiary pb-64 pt-40">
        <div className="container">
          <div className="mx-auto mw-32rem text-center">
            <SectionTag>TESTIMONIALS</SectionTag>

            <h2 className="fs-2 fw-semi-bold">Hear from our community</h2>

            <div className="fs-5 mt-8 text-body-secondary">
              WebGazer is easing monitoring for teams like yours
            </div>
          </div>
        </div>
      </div>

      <div className="mt-n64">
        <div className={classNames("d-flex overflow-auto py-24", styles.slider)} onScroll={handleSliderScroll} ref={slider}>
          {testimonials.map((d, i) => (
            <div
              className={classNames("d-flex flex-column flex-shrink-0", styles.slide)}
              key={d.person.name}
              ref={(r) => { slides.current[i] = r; }}
            >
              <div className={classNames("border-0 card d-flex h-100 rounded-4 shadow-lg", styles.slideCard)}>
                <div className="card-body d-flex flex-column p-24">
                  <div className="fs-5">
                    &quot;
                    {d.content}
                    &quot;
                  </div>

                  <div className="my-auto" />

                  <div className="align-items-center d-flex flex-column flex-sm-row gap-4 gap-sm-8 mt-12 text-center text-sm-start">
                    <img alt={d.person.name} className="h-6rem rounded-circle" src={d.person.avatar} />

                    <div className="d-flex flex-column flex-grow-0 flex-shrink-1 gap-1 overflow-hidden">
                      <div className="fs-5 fw-semi-bold">{d.person.name}</div>

                      <div>
                        <div className="d-sm-inline">{d.person.title}</div>

                        <div className="d-none d-sm-inline">{", "}</div>

                        <div className="d-sm-inline">{d.company.name}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="container">
          <div className="d-flex gap-4 justify-content-center pt-8">
            {testimonials.map((d, i) => (
              <button
                className="btn-unstyled"
                key={d.person.name}
                onClick={() => switchSlide(i)}
                type="button"
              >
                <div
                  className={classNames(
                    "bg-current-color rounded-circle size-1rem",
                    state.activeSlideIndex === i ? "text-body" : "text-body-subtle")
                  }
                  style={{ background: "currentColor" }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
